import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import {FormBuilder, FormControl} from '@angular/forms';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {debounceTime, filter, map, shareReplay, startWith, takeUntil, tap} from 'rxjs/operators';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {ServerDataSource} from '@mominsamir/ngx-smart-table';
import {PrevButtonComponent} from '@theme/components/pager-button/prev-button/prev-button.component';
import {NextButtonComponent} from '@theme/components/pager-button/next-button/next-button.component';

export enum FilterOperatorType {
    EQUAL = 'EQUAL',
    NOT_EQUAL = 'NOT_EQUAL',
    CONTAIN = 'CONTAIN',
    DOES_NOT_CONTAIN = 'DOES_NOT_CONTAIN',
    STARTS_WITH = 'STARTS_WITH',
    ENDS_WITH = 'ENDS_WITH',
    GREATER_THAN = 'GREATER_THAN',
    GREATER_THAN_OR_EQUAL = 'GREATER_THAN_OR_EQUAL',
    LESS_THAN_OR_EQUAL = 'LESS_THAN_OR_EQUAL',
    BETWEEN = 'BETWEEN',
    IN = 'IN',
    NOT_IN = 'NOT_IN',
    IS_EMPTY = 'IS_EMPTY',
    NOT_EMPTY = 'NOT_EMPTY',
}
export interface InitFilter {
    field: string;
    value: string;
}
@Component({
    selector: 'ngx-data-table',
    templateUrl: './data-table-component.html',
    styleUrls: ['./data-table-component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTableComponent extends Unsubscribable implements OnInit {
    @Input() settings: any;
    @Input() title: string;
    @Input() subTitle: string;
    @Input() initFilter: InitFilter[] = [];
    @Input() reload: Observable<number>;
    @Input() source: Observable<ServerDataSource> = new BehaviorSubject<ServerDataSource>(null);
    @Output() onCustomEvent = new EventEmitter();
    @Output() onRowSelect = new EventEmitter();
    @Output() onFilterChange = new EventEmitter();
    @Output() downloadFile = new EventEmitter();

    public data: ServerDataSource;

    public pagingFormGroup = this.fb.group({
        perPage: this.fb.control(8),
    });

    readonly perPage$: Observable<number> = this.perPageFormControl.valueChanges.pipe(
        startWith(8),
        debounceTime(500),
        map((value: number) => {
            let result: number;
            if (value < 1) {
                this.perPageFormControl.setValue(1, {emitEvent: false});
            }
            if (value > 99) {
                this.perPageFormControl.setValue(99, {emitEvent: false});
            }

            if (value > 99) result = 99;
            else if (value < 1) result = 1;
            else result = value;

            return result;
        }),
    );

    constructor(protected fb: FormBuilder, private cdr: ChangeDetectorRef) {
        super();
    }

    @ViewChild('table', {static: false}) table: ElementRef;

    ngOnInit(): void {
        if (this.settings) {
            if (this.settings.pager.mode === 'custom') {
                this.settings.pager = {
                    ...this.settings.pager,
                    prevComponent: PrevButtonComponent,
                    nextComponent: NextButtonComponent,
                };
            }
            // Reset records per page based on settings, if defined
            if (this.settings.pager && this.settings.pager.perPage) {
                this.setPerPageFormControl(this.settings.pager.perPage);
            }
        }

        combineLatest<Observable<ServerDataSource>, Observable<number>>([this.source, this.perPage$])
            .pipe(
                takeUntil(this.unsubscribe$),
                tap((dataSource) => {
                    if (dataSource[0] === null) this.data = null;
                }),
                filter((dataSource) => dataSource[0] !== null && dataSource[1] > 0),
                map(([source, perPage]: [ServerDataSource, number]) => {
                    const paging = source.getPaging();
                    if (!paging.page) {
                        source.setPaging(1, perPage, false);
                    } else if (source.getPaging().perPage !== perPage) {
                        // this block executed when per page records are changed.
                        source.setPaging(paging.page, perPage, false);
                    }
                    if (this.initFilter.length > 0) {
                        // this sets initial filters
                        this.initFilter
                            .map((d) => ({field: d.field + '_G', search: d.value}))
                            .forEach((x) => source.addFilter(x, false));
                    }
                    return source;
                }),
                debounceTime(500),
                shareReplay(1),
            )
            .subscribe((data) => {
                this.data = data;
                this.cdr.detectChanges();
            });

        this.reload.pipe(filter((data) => data !== null)).subscribe((_) => {
            if (this.data) this.data.refresh();
        });
    }

    onCustomEventClick($event): void {
        this.onCustomEvent.emit($event);
    }

    refresh = () => {
        if (this.data) this.data.refresh();
    };

    private get perPageFormControl(): FormControl {
        return this.pagingFormGroup.get('perPage') as FormControl;
    }

    private setPerPageFormControl(val: number) {
        this.perPageFormControl.reset(val);
    }

    onSearch(query: string = '') {
        Object.keys(this.settings.columns)
            .filter((k) => this.settings.columns[k].type === 'string')
            .map((k) => ({field: k + '_G', search: query}));
    }
    enableHeader() {
        if (this.settings) {
            return this.settings['enableHeader'] === undefined ? true : this.settings['enableHeader'];
        }
    }

    disableGlobalSearch = () => this.settings['enableGlobalSearch'];

    enablePagePerRecord = () =>
        this.settings['enablePagePerRecord'] === undefined ? false : this.settings['enablePagePerRecord'];
    enableTableSetting = () =>
        !this.settings['tableSettings'] || this.settings['tableSettings'] === undefined
            ? false
            : this.settings['tableSettings'];

    onUserRowSelect = ($event) => this.onRowSelect.emit($event);

    onFilter($event) {
        this.onFilterChange.emit($event);
    }
}
